import type { SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  FOCUS_MODE_BACKGROUNDS,
  FOCUS_MODE_BORDERS,
  FOCUS_MODE_BUTTON_BACKGROUNDS,
  FOCUS_MODE_COLORS,
  FOCUS_MODE_GRADIENTS,
  FOCUS_MODE_SHADOWS,
  getFocusModeValue,
} from '@src/constants/design-tokens';
import { useMemo } from 'react';

/**
 * ControlAreaスタイルのオプション
 */
interface ControlAreaStyleOptions {
  isFocusMode: boolean;
  isScrolling: boolean;
  isInputActive: boolean;
  isInline?: boolean;
  contentType?: 'resume' | 'rirekisho';
}

/**
 * ControlAreaの共通スタイルを提供するカスタムフック
 *
 * @param options - スタイルオプション
 * @returns スタイルオブジェクト
 */
export function useControlAreaStyles(options: ControlAreaStyleOptions) {
  const {
    isFocusMode,
    isScrolling,
    isInputActive,
    isInline = false,
    contentType = 'resume',
  } = options;
  const theme = useTheme();
  const prefersDarkMode = isFocusMode || theme.palette.mode === 'dark';

  const containerStyles: SxProps<Theme> = useMemo(
    () => ({
      position: isInline ? 'relative' : 'fixed',
      bottom: isInline ? 'auto' : { xs: 16, sm: 24 },
      left: isInline
        ? 'auto'
        : {
            xs: '50%',
            md: contentType === 'rirekisho' ? '20.83%' : isFocusMode ? '50%' : '20.83%',
          },
      zIndex: isInline ? 'auto' : 9998,
      backgroundColor: getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'primary', prefersDarkMode),
      backdropFilter: 'blur(12px)',
      borderRadius: { xs: 2, sm: 3 },
      boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'primary', prefersDarkMode),
      border: `2px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'primary', prefersDarkMode)}`,
      padding: { xs: '6px 10px', sm: '10px 16px' },
      width: isInline ? 'auto' : 'fit-content',
      minWidth: isInline
        ? undefined
        : {
            lg: isFocusMode ? '360px' : '320px',
          },
      maxWidth: isInline ? '100%' : undefined,
      display: isInline
        ? { xs: 'none', sm: 'none', md: 'none', lg: 'flex' }
        : { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
      opacity: isScrolling || isInputActive ? 0.4 : 1,
      transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      filter: isScrolling || isInputActive ? 'blur(0.5px)' : 'none',
      transform: isInline
        ? 'none'
        : isScrolling || isInputActive
          ? 'translateX(-50%) scale(0.95)'
          : 'translateX(-50%)',
      '&:hover': {
        opacity: 1,
        transform: isInline ? 'translateY(-2px)' : 'translateX(-50%) translateY(-4px) scale(1.02)',
        boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'hover', prefersDarkMode),
        backgroundColor: getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'darker', prefersDarkMode),
        filter: 'none',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -1,
        left: -1,
        right: -1,
        bottom: -1,
        background: getFocusModeValue(FOCUS_MODE_GRADIENTS, 'before', prefersDarkMode),
        borderRadius: 'inherit',
        zIndex: -1,
        opacity: 0,
        transition: 'opacity 0.3s ease',
      },
      '&:hover::before': {
        opacity: 1,
      },
    }),
    [prefersDarkMode, isFocusMode, isScrolling, isInputActive, isInline, contentType]
  );

  const buttonStyles = useMemo(
    () => ({
      primary: {
        backgroundColor: getFocusModeValue(
          FOCUS_MODE_BUTTON_BACKGROUNDS,
          isScrolling || isInputActive ? 'primaryLight' : 'primary',
          prefersDarkMode
        ),
        border: `1px solid ${getFocusModeValue(
          FOCUS_MODE_BORDERS,
          isScrolling || isInputActive ? 'buttonLight' : 'button',
          prefersDarkMode
        )}`,
        color: getFocusModeValue(
          FOCUS_MODE_COLORS,
          isScrolling || isInputActive ? 'primaryLight' : 'primary',
          prefersDarkMode
        ),
        opacity: isScrolling || isInputActive ? 0.5 : 1,
        '&:hover': {
          backgroundColor: getFocusModeValue(
            FOCUS_MODE_BUTTON_BACKGROUNDS,
            'primaryHover',
            prefersDarkMode
          ),
          border: `1px solid ${getFocusModeValue(
            FOCUS_MODE_BORDERS,
            'buttonHover',
            prefersDarkMode
          )}`,
          transform: 'scale(1.05)',
          boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'button', prefersDarkMode),
          opacity: 1,
        },
        '&:disabled': {
          backgroundColor: getFocusModeValue(
            FOCUS_MODE_BUTTON_BACKGROUNDS,
            'disabled',
            prefersDarkMode
          ),
          border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'disabled', prefersDarkMode)}`,
          color: getFocusModeValue(FOCUS_MODE_COLORS, 'disabled', prefersDarkMode),
        },
        transition: 'all 0.2s ease',
      },
      download: {
        backgroundColor: getFocusModeValue(
          FOCUS_MODE_BUTTON_BACKGROUNDS,
          isScrolling || isInputActive ? 'downloadLight' : 'download',
          prefersDarkMode
        ),
        border: `1px solid ${getFocusModeValue(
          FOCUS_MODE_BORDERS,
          isScrolling || isInputActive ? 'downloadLight' : 'download',
          prefersDarkMode
        )}`,
        color: getFocusModeValue(
          FOCUS_MODE_COLORS,
          isScrolling || isInputActive ? 'downloadLight' : 'download',
          prefersDarkMode
        ),
        opacity: isScrolling || isInputActive ? 0.5 : 1,
        '&:hover': {
          backgroundColor: getFocusModeValue(
            FOCUS_MODE_BUTTON_BACKGROUNDS,
            'downloadHover',
            prefersDarkMode
          ),
          border: `1px solid ${getFocusModeValue(
            FOCUS_MODE_BORDERS,
            'downloadHover',
            prefersDarkMode
          )}`,
          transform: 'scale(1.05)',
          boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'download', prefersDarkMode),
          opacity: 1,
        },
        '&:disabled': {
          backgroundColor: getFocusModeValue(
            FOCUS_MODE_BUTTON_BACKGROUNDS,
            'disabled',
            prefersDarkMode
          ),
          border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'disabled', prefersDarkMode)}`,
          color: getFocusModeValue(FOCUS_MODE_COLORS, 'disabled', prefersDarkMode),
        },
        transition: 'all 0.2s ease',
      },
      scroll: {
        backgroundColor: getFocusModeValue(
          FOCUS_MODE_BUTTON_BACKGROUNDS,
          isScrolling || isInputActive ? 'scrollLight' : 'scroll',
          prefersDarkMode
        ),
        border: `1px solid ${getFocusModeValue(
          FOCUS_MODE_BORDERS,
          isScrolling || isInputActive ? 'scrollLight' : 'scroll',
          prefersDarkMode
        )}`,
        color: getFocusModeValue(
          FOCUS_MODE_COLORS,
          isScrolling || isInputActive ? 'scrollLight' : 'scroll',
          prefersDarkMode
        ),
        opacity: isScrolling || isInputActive ? 0.5 : 1,
        '&:hover': {
          backgroundColor: getFocusModeValue(
            FOCUS_MODE_BUTTON_BACKGROUNDS,
            'scrollHover',
            prefersDarkMode
          ),
          border: `1px solid ${getFocusModeValue(
            FOCUS_MODE_BORDERS,
            'scrollHover',
            prefersDarkMode
          )}`,
          transform: 'scale(1.05)',
          boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'scroll', prefersDarkMode),
          opacity: 1,
        },
        transition: 'all 0.2s ease',
      },
    }),
    [prefersDarkMode, isScrolling, isInputActive]
  );

  return {
    containerStyles,
    buttonStyles,
  };
}

export type ControlAreaStyles = ReturnType<typeof useControlAreaStyles>;
